// Emergency Numbers → the plan for one domain and the gates around changing it:
// manager decision (trunk + users) → approval (type the domain) → dry run → execute.
import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FlaskConical, Loader2, MapPinOff, Play, ShieldAlert, TriangleAlert } from "lucide-react";
import api from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MODE, Pill, ago, clock } from "./ui";

const STEP_TONE = (step) => (step.startsWith("delete") ? "bg-rose-100 text-rose-800"
  : step.startsWith("create") ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700");

function Decision({ doc, canEdit, onChanged, onReassess }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const path = `/e911/domains/${doc.platform}/${encodeURIComponent(doc.domain)}/decision`;

  const decide = async (mode) => {
    setBusy(mode || "clear");
    try {
      await api.post(path, { mode, note });
      toast.success(mode ? `${doc.domain} will be treated as ${MODE[mode].label}` : "Decision cleared");
      await onChanged();
      await onReassess();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record the decision");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-md border border-sky-200 bg-sky-50/60 px-3 py-3" data-testid="e911-decision">
      <div className="flex items-start gap-2 text-sm text-sky-900">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <strong>Manager decision.</strong> This domain has a SIP trunk <em>and</em> {doc.regular_users} regular
          user{doc.regular_users === 1 ? "" : "s"}, so it can legitimately be served statically (trunk) or dynamically
          (users). Nothing is planned until someone decides.
          {doc.decision && <> Current decision: <strong>{MODE[doc.decision]?.long || doc.decision}</strong>.</>}
        </div>
      </div>
      {canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input className="h-8 w-64 bg-white text-[12px]" placeholder="Why (optional)" value={note}
                 onChange={(e) => setNote(e.target.value)} data-testid="e911-decision-note" />
          <Button size="sm" variant={doc.decision === "LEGACY" ? "default" : "outline"} disabled={!!busy}
                  onClick={() => decide("LEGACY")} data-testid="e911-decision-legacy">
            {busy === "LEGACY" && <Loader2 className="h-4 w-4 animate-spin" />} Static (Legacy)
          </Button>
          <Button size="sm" variant={doc.decision === "DLR" ? "default" : "outline"} disabled={!!busy}
                  onClick={() => decide("DLR")} data-testid="e911-decision-dlr">
            {busy === "DLR" && <Loader2 className="h-4 w-4 animate-spin" />} DLR (dynamic)
          </Button>
          {doc.decision && (
            <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => decide("")} data-testid="e911-decision-clear">
              Clear decision
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Approval({ doc, canEdit, onChanged, onShowRun }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  const base = `/e911/domains/${doc.platform}/${encodeURIComponent(doc.domain)}`;
  const a = doc.approval;
  const current = a && a.status === "approved" && a.plan_fingerprint === doc.plan_fingerprint;
  const ageH = a?.assessed_at ? (Date.now() - new Date(a.assessed_at).getTime()) / 36e5 : null;

  const approve = async () => {
    setBusy("approve");
    try {
      await api.post(`${base}/approve`, { fingerprint: doc.plan_fingerprint, typed_domain: typed });
      toast.success(`Plan for ${doc.domain} approved`);
      setTyped("");
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve");
    } finally {
      setBusy("");
    }
  };

  const fix = async (execute) => {
    setBusy(execute ? "execute" : "dry");
    try {
      const { data } = await api.post(`${base}/fix`, { execute });
      toast.success(`${execute ? "Fix" : "Dry run"} ${data.run?.run_id} started`);
      await onChanged();
      onShowRun?.(data.run?.run_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the run");
    } finally {
      setBusy("");
      setConfirm(false);
    }
  };

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-3" data-testid="e911-approval">
      <div className="text-sm text-amber-900">
        <strong>{doc.planned_actions.length} action{doc.planned_actions.length === 1 ? "" : "s"} planned, no blockers.</strong>{" "}
        Plan <span className="font-mono text-[11px]">{doc.plan_fingerprint}</span>.
        {a && (
          <span data-testid="e911-approval-state">
            {" "}{a.status === "executed" ? `Executed ${ago(a.executed_at)} by ${a.by}.`
              : a.status === "refused" ? `Last attempt refused: ${a.reason}.`
                : current ? `Approved by ${a.by} ${ago(a.approved_at)}.`
                  : `An earlier approval (${a.plan_fingerprint}) no longer matches this plan — approve again.`}
          </span>
        )}
        {current && ageH > 20 && <span className="text-rose-700"> The approval is {Math.round(ageH)} h old; execution refuses anything older than the limit in Settings.</span>}
      </div>
      {canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => fix(false)} data-testid="e911-dry-run">
            {busy === "dry" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />} Dry run
          </Button>
          {current ? (
            <Button size="sm" disabled={!!busy} onClick={() => setConfirm(true)} data-testid="e911-execute">
              {busy === "execute" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Execute the fix
            </Button>
          ) : (
            <>
              <Input className="h-8 w-56 bg-white font-mono text-[12px]" placeholder={`type ${doc.domain}`} value={typed}
                     onChange={(e) => setTyped(e.target.value)} data-testid="e911-approve-typed" />
              <Button size="sm" disabled={!!busy || typed.trim().toLowerCase() !== doc.domain.toLowerCase()} onClick={approve}
                      data-testid="e911-approve">
                {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve this plan
              </Button>
            </>
          )}
        </div>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="e911-execute-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Change live 911 configuration on {doc.domain}?</AlertDialogTitle>
            <AlertDialogDescription>
              The domain is re-assessed first; the fix only runs if the plan still fingerprints identically and has no
              blockers. Every destructive step is gated on a proven replacement and the one unavoidable gap is wrapped
              in a rollback. The log lands under Runs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="e911-execute-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => fix(true)} data-testid="e911-execute-go">Execute</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function DomainPlan({ doc, canEdit, onChanged, onReassess, onShowRun }) {
  const showDecision = doc.has_siptrunk && doc.regular_users > 0 && !doc.excepted;
  const approvable = !doc.decision_required && !doc.blockers?.length && !!doc.planned_actions?.length;
  return (
    <div className="space-y-3" data-testid="e911-plan">
      {showDecision && <Decision doc={doc} canEdit={canEdit} onChanged={onChanged} onReassess={onReassess} />}

      {doc.excepted && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600" data-testid="e911-plan-hybrid">
          On the hybrid exception list — this domain deliberately runs Legacy and DLR together and is never planned.
        </div>
      )}

      {doc.address_missing && (
        <div className="rounded-md border border-orange-200 bg-orange-50/70 px-3 py-2 text-sm text-orange-900" data-testid="e911-address-missing">
          <div className="flex items-start gap-2">
            <MapPinOff className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <strong>Address missing.</strong>{" "}
              {doc.subscribers?.address?.none
                ? <>{doc.subscribers.address.none} of {doc.subscribers.regular} real user{doc.subscribers.regular === 1 ? "" : "s"} have no emergency address of their own
                    {doc.domain_default?.address_state === "none" ? " and the domain default is not set" : " and the domain default points at an unknown address"}. </>
                : null}
              {!!doc.subscribers?.address?.unknown && <>{doc.subscribers.address.unknown} point at an address-id this domain does not hold. </>}
              {!!doc.subscribers?.bad_number?.length && <>{doc.subscribers.bad_number.length} have an emergency number that is not one of the domain's Emergency Endpoints. </>}
              {canEdit
                ? "Set the domain default in the Subscribers table below (every user without an address inherits it), or fix the users one by one."
                : "The registrations themselves are fine; a manager has to point the subscribers at them."}
            </div>
          </div>
        </div>
      )}

      {!!doc.diagnosis?.length && (
        <div data-testid="e911-diagnosis">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">What looks wrong</div>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
            {doc.diagnosis.map((d, i) => <li key={i}>{d}</li>)}
          </ol>
        </div>
      )}

      {!!doc.notes?.length && (
        <div data-testid="e911-notes" className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700">Worth a look · verdict unchanged</div>
          <ul className="list-disc space-y-1 pl-5 text-sm text-amber-900">
            {doc.notes.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </div>
      )}

      {!!doc.blockers?.length && (
        <div className="rounded-md border border-rose-200 bg-rose-50/70 px-3 py-2" data-testid="e911-blockers">
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
            <TriangleAlert className="h-3.5 w-3.5" /> Blocks any automatic fix
          </div>
          <ul className="space-y-1 text-sm text-rose-900">
            {doc.blockers.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        </div>
      )}

      {!!doc.planned_actions?.length && (
        <div data-testid="e911-actions">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            The plan{doc.blockers?.length ? " (held back by the blockers above)" : ""}
          </div>
          <ol className="space-y-1.5">
            {doc.planned_actions.map((a, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="mt-0.5 w-4 shrink-0 text-right text-[11px] text-slate-400">{i + 1}</span>
                <div>
                  <Pill tone={STEP_TONE(a.step)}>{a.step}</Pill>
                  {a.tn && <span className="ml-1.5 font-mono text-[12px] text-slate-800">{a.tn}</span>}
                  <span className="ml-1.5">{a.explain}</span>
                  {a.api && <div className="font-mono text-[10px] text-slate-400">{a.api}</div>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {doc.already_correct && !doc.planned_actions?.length && !doc.excepted && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
             data-testid="e911-plan-correct">
          <CheckCircle2 className="h-4 w-4" /> Set up exactly as a {MODE[doc.expected_mode]?.label || doc.expected_mode} domain should be.
        </div>
      )}

      {approvable && <Approval doc={doc} canEdit={canEdit} onChanged={onChanged} onShowRun={onShowRun} />}

      {!!doc.fix_runs?.length && (
        <div data-testid="e911-fix-runs">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Fix runs on this domain</div>
          <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white text-sm">
            {doc.fix_runs.map((r) => (
              <li key={r.run_id} className="flex flex-wrap items-center gap-2 px-3 py-1.5">
                <Pill tone={r.execute ? "bg-rose-100 text-rose-800" : "bg-slate-200 text-slate-700"}>{r.execute ? "execute" : "dry run"}</Pill>
                <Pill tone={r.status === "done" ? "bg-emerald-100 text-emerald-800" : r.status === "running" ? "bg-sky-100 text-sky-800" : "bg-rose-100 text-rose-800"}>{r.status}</Pill>
                <span className="text-slate-600">{clock(r.started_at)} UTC · {r.by}</span>
                {r.summary?.result && <span className="text-[12px] text-slate-500">{r.summary.result}{r.summary.reason ? ` — ${r.summary.reason}` : ""}</span>}
                <button type="button" onClick={() => onShowRun?.(r.run_id)} className="ml-auto text-xs font-medium text-primary hover:underline"
                        data-testid={`e911-fix-run-log-${r.run_id}`}>Log</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
